<i18n
  src="@/locales/views/organization/partners/query_producers/list_producers.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>

<template>
  <bread-crumbs :labels="[module_name]" />
  <template v-if="load_data_general">
    <div class="mt-8 flex flex-row justify-between">
      <div class="panel-plain-label space-x-2">
        <badge-default
          :label="producers.length + ' ' + $t('labels.total_number_producers')"
        />
        <badge-default
          :label="total_hectares + ' ' + $t('labels.total_hectares')"
        />
        <badge-default
          :label="total_square_meters + ' ' + $t('labels.total_square_meters')"
        />
      </div>

      <div>
        <button
          type="button"
          @click.prevent="downloadRepCropFieldsXlsx()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              downloading_crop_field ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          ></i
          ><span class="space-icon"></span
          >{{ $t("labels.download_crop_fields_report_button") }}
        </button>
        <span class="space-actions" />
        <button
          type="button"
          @click.prevent="downloadRepProducersXlsx()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              downloading ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          ></i
          ><span class="space-icon"></span
          >{{ $t("labels.download_producers_report_button") }}
        </button>
        <span class="space-actions" />
      </div>
    </div>

    <div class="mt-8 flex flex-col">
      <ag-grid-vue
        style="width: 100%; height: 650px"
        class="ag-theme-alpine"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="producers"
      >
      </ag-grid-vue>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import GoToLink from "@/components/views/layout/ag_grid/GoToLink.vue";

import Requester from "@/views/organization/requesters/partners/query_producers/list_producers.js";

export default {
  name: "OrgPartnerQueryProducersListProducers",
  components: { AgGridVue },
  data() {
    return {
      load_data_general: false,
      producers: [],
      downloading: false,
      downloading_crop_field: false,
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.$t(
            "model.producer.organization_producer.producer_code"
          ),
          field: "organization_producer.producer_code",
          filter: true,
          cellRendererSelector: params => {
            "org_partners_producers_show_producer_path"
            return {
              component: GoToLink,
              params: {
                text: params.value,
                path_name: "org_partners_query_producers_show_producer_path",
                params_path: {
                  organization_id: this.organization_id,
                  producer_id: params.data.id
                }
              }
            };
          }
        },
        {
          headerName: this.$t("model.producer.producer_code"),
          field: "producer_code",
          filter: true,
          cellRendererSelector: params => {
            return {
              component: GoToLink,
              params: {
                text: params.value,
                path_name: "org_partners_query_producers_show_producer_path",
                params_path: {
                  organization_id: this.organization_id,
                  producer_id: params.data.id
                }
              }
            };
          }
        },
        {
          headerName: this.$t("model.producer.lastname"),
          field: "lastname",
          filter: true,
          width: 300
        },
        {
          headerName: this.$t("model.producer.name"),
          field: "name",
          filter: true,
          width: 300
        },
        {
          headerName: this.$t("model.producer.producer_crops"),
          field: "producer_crops",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.hectares"),
          field: "hectares",
          filter: 'agNumberColumnFilter',
          cellRenderer: (params) => {
            return this.numberWithPrecision(params.value)
          }
        },
        {
          headerName: this.$t("model.crop_field.square_meters"),
          field: "square_meters",
          filter: 'agNumberColumnFilter',
          cellRenderer: (params) => {
            return this.numberWithPrecision(params.value)
          }
        },
        {
          headerName: this.$t("model.producer.organization_producer.active"),
          field: "organization_producer.active",
          filter: true,
          cellRendererSelector: params => {
            return {
              component: {
                template: `<yes-no-label :value="${params.value}"></yes-no-label>`
              }
            };
          }
        }
      ]
    };
  },
  props: {
    organization_id: {
      required: true
    }
  },
  computed: {
    total_hectares: function () {
      let total = this.producers.reduce(
        (sum, producer) => sum + producer.hectares,
        0
      );
      return this.numberWithPrecision(total);
    },
    total_square_meters: function () {
      let total = this.producers.reduce(
        (sum, producer) => sum + producer.square_meters,
        0
      );
      return this.numberWithPrecision(total);
    }
  },
  methods: {
    fetchProducers: async function () {
      let response = await Requester.fetchProducers(this.organization_id);
      this.producers = response.data;
      this.producers.forEach(producer => {
        producer.producer_crops = this.producerCrops(producer);
      });
      this.load_data_general = true;
    },
    producerCrops: function (producer) {
      return producer.producer_crops
        .map(producer_crop => producer_crop.crop.name)
        .join(", ");
    },
    downloadRepProducersXlsx: async function () {
      this.downloading = true;
      try {
        var response = await Requester.downloadRepProducersXlsx(
          this.organization_id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute("download", "Productores.xlsx");
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        error;
      }
      this.downloading = false;
    },
    downloadRepCropFieldsXlsx: async function () {
      this.downloading_crop_field = true;
      try {
        var response = await Requester.downloadRepCropFieldsXlsx(
          this.organization_id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute("download", "Productores y Campos.xlsx");
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        error;
      }
      this.downloading_crop_field = false;
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => this.fetchProducers())
    );
  }
};
</script>
